import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { EMPLOYEE_VALUATION_OPTIONS } from "../../../../../../util/defaults/contrato";
import { useUpdateContractEmployeeValuation } from "../../api/updateContractEmployeeValuation";

const StyledContractName = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.875rem;
  margin: 0 0 1rem;
`;

const StyledErrorAlert = styled(Alert)`
  margin: 1rem 0 0;
`;

const StyledButtonProgress = styled(CircularProgress)`
  margin-left: 0.5rem;
`;

const initialValue = (currentValue?: string) => {
  if (currentValue && EMPLOYEE_VALUATION_OPTIONS.indexOf(currentValue) !== -1) {
    return currentValue;
  }

  return "";
};

type TEditEmployeeValuationDialogProps = {
  open: boolean;
  contractId: string;
  contractName?: string;
  currentValue?: string;
  onClose: () => void;
};

const EditEmployeeValuationDialog = ({
  open,
  contractId,
  contractName,
  currentValue,
  onClose,
}: TEditEmployeeValuationDialogProps) => {
  const [value, setValue] = useState(initialValue(currentValue));
  const mutation = useUpdateContractEmployeeValuation();
  const { reset } = mutation;

  useEffect(() => {
    if (open) {
      setValue(initialValue(currentValue));
      reset();
    }
  }, [open, currentValue, reset]);

  const handleSave = () => {
    mutation.mutate(
      { contractId, Valoracion_Cuideo_Candidata: value },
      { onSuccess: onClose }
    );
  };

  const isSaveDisabled =
    value === "" || value === currentValue || mutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="edit-employee-valuation-dialog-title"
    >
      <DialogTitle id="edit-employee-valuation-dialog-title">
        <FormattedMessage
          id="EmployeeValuation.Editar valoración de la candidata"
          defaultMessage="Editar valoración de la candidata"
        />
      </DialogTitle>
      <DialogContent>
        {contractName && (
          <StyledContractName>{contractName}</StyledContractName>
        )}
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          id="employee-valuation"
          name="employeeValuation"
          label={
            <FormattedMessage
              id="EmployeeValuation.Valoración"
              defaultMessage="Valoración"
            />
          }
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={mutation.isLoading}
        >
          {EMPLOYEE_VALUATION_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        {mutation.isError && (
          <StyledErrorAlert severity="error">
            <FormattedMessage
              id="EmployeeValuation.No se ha podido guardar la valoración"
              defaultMessage="No se ha podido guardar la valoración"
            />
          </StyledErrorAlert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isLoading}>
          <FormattedMessage
            id="EmployeeValuation.Cancelar"
            defaultMessage="Cancelar"
          />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          <FormattedMessage
            id="EmployeeValuation.Guardar"
            defaultMessage="Guardar"
          />
          {mutation.isLoading && (
            <StyledButtonProgress size={16} thickness={5} color="inherit" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmployeeValuationDialog;
